import { BasePagination, IconButton, Text } from '@elkaso-app/web-design';
import { ICrmMasterCuisineData, useGetCrmMasterCuisinesApi } from 'apis/crm-master-cuisines';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { CountryFilterButton } from '../components/filters/country-filter';
import { NameFilterButton } from '../components/filters/name-filter';
import { RegionFilterButton } from '../components/filters/region-filter';
import { StatusFilterButton } from '../components/filters/status-filter';
import { CreateMasterCuisineModal } from '../components/modals/create-master-cuisine-modal';
import { DeleteMasterCuisineModal } from '../components/modals/delete-master-cuisine-modal';
import { EditMasterCuisineModal } from '../components/modals/edit-master-cuisine-modal';
import { ActionsCell, IActionsCell } from '../components/table-cells/actions-cell';
import { DateCell, IDateCell } from '../components/table-cells/date-cell';
import { ILocationCell, LocationCell } from '../components/table-cells/location-cell';
import { IStatusCell, StatusCell } from '../components/table-cells/status-cell';

const CrmMasterCuisinesListScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const setPaginationParams = useSetPaginationParamsV1();

  const [selectedMasterCuisine, setSelectedMasterCuisine] = useState<ICrmMasterCuisineData | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data, isFetching, isLoading } = useGetCrmMasterCuisinesApi();

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Master Cuisines'));
  }, [dispatch]);

  // Memoized handlers to prevent recreating on every render
  const handleEdit = useCallback((masterCuisine: ICrmMasterCuisineData) => {
    setSelectedMasterCuisine(masterCuisine);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((masterCuisine: ICrmMasterCuisineData) => {
    setSelectedMasterCuisine(masterCuisine);
    setIsDeleteModalOpen(true);
  }, []);

  // Memoized modal close handlers
  const handleCreateModalClose = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedMasterCuisine(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedMasterCuisine(null);
  }, []);

  const handleCreateModalOpen = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  // Memoized columns to prevent recreation
  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='Name' filterButtonComponent={<NameFilterButton />} />,
        accessor: 'name',
        width: 200,
        Cell: ({ row }: CellProps<any, any>) => (
          <Text fontWeight='semibold' fontSize='md' color='gray.800'>
            {row.original.name}
          </Text>
        ),
      },
      {
        Header: <TableHeader name='Location' filterButtonComponent={<RegionFilterButton />} />,
        accessor: 'region',
        width: 250,
        minWidth: 200,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<ILocationCell>
            as={LocationCell}
            region={row.original.region}
            country={row.original.country}
          />
        ),
      },
      {
        Header: <TableHeader name='Country' filterButtonComponent={<CountryFilterButton />} />,
        accessor: 'country',
        width: 150,
        Cell: ({ row }: CellProps<any, any>) => (
          <Text fontSize='sm' color='gray.600'>
            {row.original.country || 'Not specified'}
          </Text>
        ),
      },
      {
        Header: <TableHeader name='Created & Updated' />,
        accessor: 'createdAt',
        width: 200,
        minWidth: 180,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IDateCell>
            as={DateCell}
            createdAt={row.original.createdAt}
            updatedAt={row.original.updatedAt}
          />
        ),
      },
      {
        Header: <TableHeader name='Status' filterButtonComponent={<StatusFilterButton />} />,
        accessor: 'isActive',
        width: 150,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IStatusCell> as={StatusCell} isActive={row.original.isActive} cuisineId={row.original.id} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'id',
        width: 120,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IActionsCell>
            as={ActionsCell}
            masterCuisine={row.original}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ),
      },
    ],
    [handleEdit, handleDelete]
  );

  // Memoized table data
  const tableData = useMemo(() => data?.data || [], [data?.data]);

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='CRM Master Cuisines' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <IconButton
            aria-label='Add Master Cuisine'
            icon={<FiPlus />}
            colorScheme='red'
            onClick={handleCreateModalOpen}
          />
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={data?.pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={data?.total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>

      {/* Self-contained modals */}
      <CreateMasterCuisineModal isOpen={isCreateModalOpen} onClose={handleCreateModalClose} />

      {selectedMasterCuisine && (
        <>
          <EditMasterCuisineModal
            isOpen={isEditModalOpen}
            onClose={handleEditModalClose}
            masterCuisine={selectedMasterCuisine}
          />
          <DeleteMasterCuisineModal
            isOpen={isDeleteModalOpen}
            onClose={handleDeleteModalClose}
            masterCuisine={selectedMasterCuisine}
          />
        </>
      )}
    </BaseTableContainer>
  );
};

export default CrmMasterCuisinesListScreen;
